import axios from "axios";
import appConfig from "../appConfig";
import { buildRequestHeaders } from "./headers";

/** The fields of the API's `UserInfo` that this app reads. */
export interface BffUser {
  email: string;
  displayName: string;
  instanceId: string;
  roles: string[];
}

const bffClient = axios.create({
  baseURL: appConfig.BFF_ENDPOINT,
  // The session is an HttpOnly cookie the browser attaches itself. Same-origin would be
  // enough in every deployed environment, but not for a developer pointing
  // API_ENDPOINT at another host.
  withCredentials: true,
  headers: buildRequestHeaders(),
});

/**
 * Sends the user to Okta through the API, which holds the OIDC client.
 *
 * The API validates `returnUrl` is a path on its own origin and carries it inside the
 * DataProtection-protected OIDC `state`, so it cannot be tampered with at Okta. That is
 * what replaces stashing the path in `localStorage` before the redirect.
 */
export const redirectToLogin = (): void => {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams({
    returnUrl: `${pathname}${search}${hash}`,
  });

  window.location.assign(`${appConfig.BFF_ENDPOINT}/login?${params.toString()}`);
};

/**
 * Reads the session. No external call happens behind it - everything was resolved once,
 * during the sign-in redirect. Null means the API does not recognise the cookie.
 */
export const getUser = async (): Promise<BffUser | null> => {
  try {
    const response = await bffClient.get("/me");
    return response.data?.user ?? null;
  } catch {
    return null;
  }
};

/**
 * Ends the session and returns Okta's end-session URL for the caller to navigate to.
 * Revocation and session deletion happen server-side and are awaited. Falls back to the
 * app root, so a failure here still gets the user off the page.
 */
export const logout = async (): Promise<string> => {
  try {
    const response = await bffClient.post("/logout");
    return response.data?.endSessionUrl || "/";
  } catch {
    return "/";
  }
};
